import React from 'react';
import * as Immutable from 'immutable';
import classNames from 'classnames';
import {
  ContentBlock,
  convertFromRaw,
  convertToRaw,
  DefaultDraftBlockRenderMap,
  EditorBlock,
  EditorState,
  Modifier,
  RawDraftContentState,
  RichUtils,
} from 'draft-js';
import { Editor, SyntheticKeyboardEvent } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { token } from '@bynder/design-system';
import { WIDGET_DEBOUNCE_WHILE_TYPING } from '../../constants/app';
import { useGetThemesetColors } from '../../queries/themesets';
import { defaultToolbarOptions } from './RichText/Config';
import { useTextWidgetMutation } from '../../mutations/widgets/text';
import { CustomTitleBlock } from './RichText/CustomTitleBlock';
import { getBlockFirstCharHexColor } from '../../constants/widgets';

interface Props {
  widgetId: string;
  sectionId: string;
  columnIndex: number;
  content?: RawDraftContentState;
  readOnly?: boolean;
  autofocus?: boolean;
  inlineContent?: boolean;
  maxLength?: number;
  placeholder: string;
}

const RichText: React.FunctionComponent<Props> = ({
  sectionId,
  widgetId,
  columnIndex,
  readOnly,
  autofocus,
  maxLength,
  placeholder,
  inlineContent,
  content: propContent,
}) => {
  const editorEl = React.useRef(null);
  const [editorState, setEditorState] = React.useState(
    EditorState.createEmpty(),
  );

  const themesetColors = useGetThemesetColors();
  const { editTextWidget } = useTextWidgetMutation(
    sectionId,
    widgetId,
    columnIndex,
  );

  const isValidLength = (text: string): boolean => text.length <= maxLength;
  const hasColors = !!themesetColors.length;

  const toolbarOptions = {
    ...defaultToolbarOptions(hasColors),
  };

  React.useEffect(() => {
    if (!readOnly && autofocus) {
      editorEl.current?.focusEditor();
    }
  }, [autofocus, readOnly, editorEl]);

  React.useEffect(() => {
    if (propContent) {
      setEditorState(
        EditorState.createWithContent(convertFromRaw(propContent)),
      );
    }
  }, []);

  const updateWidget = useDebouncedCallback(
    React.useCallback(
      (content: RawDraftContentState) => {
        editTextWidget.mutate({ widgetText: content });
      },
      [editTextWidget],
    ),
    WIDGET_DEBOUNCE_WHILE_TYPING,
  );

  React.useEffect(
    () => () => {
      updateWidget.flush();
    },
    [updateWidget],
  );
  const onHandleChange = (newEditorState: EditorState) => {
    const newContent = newEditorState.getCurrentContent();
    const currentContent = editorState.getCurrentContent();

    if (currentContent !== newContent) {
      updateWidget(convertToRaw(newEditorState.getCurrentContent()));
    }

    setEditorState(newEditorState);
  };
  const blockRenderMap = Immutable.Map({
    'ordered-list-item': {
      element: 'li',
      wrapper: <CustomOrderedListWrapper />,
    },
  });

  const onHandlePaste = (text: string) => {
    const newText = text + editorState.getCurrentContent().getPlainText();

    if (isValidLength(newText)) {
      return false;
    }

    return true;
  };

  const blockRenderer = (contentBlock: ContentBlock) => {
    const blockType = contentBlock.getType();

    if (blockType === 'unordered-list-item') {
      const firstCharColor = getBlockFirstCharHexColor(contentBlock);

      return {
        component: CustomUnorderedListElement,
        editable: !readOnly,
        props: {
          firstCharColor,
        },
      };
    }

    if (blockType === 'header-two') {
      return {
        component: CustomTitleBlock,
        editable: !readOnly,
        props: {
          readOnly,
        },
      };
    }

    return false;
  };

  /* Handle the characters to be inserted from a beforeInput event.
  Returning 'handled' causes the default behavior of the beforeInput
  event to be prevented (i.e. it is the same as calling the preventDefault method on the event).
  Example usage: After a user has typed - at the start of a new block,
  you might convert that ContentBlock into an unordered-list-item.*/
  const onHandleBeforeInput = (text = '') => {
    const newText = text + editorState.getCurrentContent().getPlainText();

    if (isValidLength(newText)) {
      return false;
    }

    const selectionState = editorState.getSelection();

    if (!selectionState.isCollapsed()) {
      const newContent = Modifier.replaceText(
        editorState.getCurrentContent(),
        selectionState,
        text,
      );

      onHandleChange(
        EditorState.push(editorState, newContent, 'insert-characters'),
      );
    }

    return true;
  };

  const shouldHandleReturn = (event: SyntheticKeyboardEvent) => {
    if (event.shiftKey) {
      setEditorState(RichUtils.insertSoftNewline(editorState));

      return true;
    }

    const currentContentHasValidLength = isValidLength(
      editorState.getCurrentContent().getPlainText(),
    );

    return inlineContent || !currentContentHasValidLength;
  };

  const shouldHidePlaceholder = () => {
    const contentState = editorState.getCurrentContent();

    return !(
      contentState.hasText() ||
      contentState.getBlockMap().first().getType() !== 'unstyled'
    )
      ? placeholder
      : '';
  };

  if (readOnly && !propContent) {
    return null;
  }

  const extendedBlockRenderMap =
    DefaultDraftBlockRenderMap.merge(blockRenderMap);

  return (
    <div
      data-testid="text-widget"
      className={classNames({
        'inline-draft': !readOnly,
        'disabled-draft': readOnly,
      })}
    >
      <Editor
        ref={editorEl}
        spellCheck
        placeholder={shouldHidePlaceholder()}
        readOnly={readOnly}
        editorState={editorState}
        onEditorStateChange={onHandleChange}
        toolbar={toolbarOptions}
        toolbarOnFocus
        toolbarHidden={inlineContent || readOnly}
        handlePastedText={onHandlePaste}
        onBlur={() => updateWidget.flush()}
        handleBeforeInput={onHandleBeforeInput}
        blockRendererFn={blockRenderer}
        blockRenderMap={extendedBlockRenderMap}
        handleReturn={shouldHandleReturn}
        wrapperClassName="editor-wrapper"
        editorClassName="editor-editor"
        toolbarClassName="editor-toolbar"
      />
    </div>
  );
};

// TODO MEMOIZE
export default RichText;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomUnorderedListElement = (props: any) => {
  return (
    <StyledBulletPoint color={props.blockProps.firstCharColor}>
      <EditorBlock {...props} />
    </StyledBulletPoint>
  );
};

const StyledBulletPoint = styled.div`
  display: -webkit-inline-box;
  &:before {
    content: '\\2022';
    color: ${props => (props.color ? props.color : token.colorText)};
    width: 1em;
    margin-left: -1em;
  }
`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomOrderedListWrapper = (props: any) => {
  const color = getBlockFirstCharHexColor(
    props.children[0].props.children.props.block,
  );

  return <StyledOrderedList color={color}>{props.children}</StyledOrderedList>;
};

const StyledOrderedList = styled.div`
  color: ${props => (props.color ? props.color : token.colorText)};
`;
